import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import BRAND from "../../brand";
import { Mic, ChevronRight, Play, ArrowRight, Menu, X } from "lucide-react";

export function HeroSection({ mobileMenuOpen, setMobileMenuOpen }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCTA = () => {
    navigate(user ? "/dashboard" : "/login");
  };

  return (
    <>
      {/* NAV */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-6 sm:h-8 w-auto flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="text-base sm:text-lg font-bold text-[#0B1F3B] tracking-tight truncate">{BRAND.productName}</span>
            </div>
            <div className="hidden sm:flex items-center gap-3">
              {user ? (
                <Button onClick={() => navigate("/dashboard")} className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 rounded-full px-6" data-testid="nav-dashboard-btn">
                  Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" className="text-[#0B1F3B] font-medium" data-testid="nav-login-btn">Log In</Button></Link>
                  <Link to="/register"><Button className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 rounded-full px-6" data-testid="nav-register-btn">Get Started</Button></Link>
                </>
              )}
            </div>
            <button className="sm:hidden p-2 -mr-2 text-[#0B1F3B]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="sm:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3" data-testid="mobile-menu">
            {user ? (
              <Button onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }} className="w-full bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 rounded-full h-12">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block">
                  <Button variant="outline" className="w-full h-12 rounded-full text-[#0B1F3B] font-medium border-slate-200">Log In</Button>
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block">
                  <Button className="w-full bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 rounded-full h-12">Get Started</Button>
                </Link>
              </>
            )}
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-5 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="fade-up text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#14B8A6]/10 text-[#14B8A6] rounded-full text-xs sm:text-sm font-medium mb-5 sm:mb-6">
                <Mic className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Voice-First AI Platform
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-[#0B1F3B] leading-[1.1] tracking-tight mb-5 sm:mb-6">
                Practice.<br className="hidden sm:block" />
                <span className="text-[#2563EB]"> Improve.</span><br className="hidden sm:block" />
                Perform.
              </h1>
              <p className="text-base sm:text-lg text-slate-500 leading-relaxed max-w-lg mx-auto lg:mx-0 mb-8 sm:mb-10">
                {BRAND.positioning.description}. Built for life insurance advisors in India.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <Button onClick={handleCTA} className="w-full sm:w-auto bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 h-12 sm:h-14 px-8 text-base rounded-full btn-premium" data-testid="hero-cta-btn">
                  <Play className="w-5 h-5 mr-2" /> Start Practice
                </Button>
                <a href="#how-it-works" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B] transition-colors flex items-center gap-1">
                  See how it works <ChevronRight className="w-4 h-4" />
                </a>
              </div>
              <div className="flex items-center justify-center lg:justify-start gap-6 sm:gap-8 mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-slate-100">
                <div><p className="text-xl sm:text-2xl font-bold text-[#0B1F3B]">&lt;2.5s</p><p className="text-[10px] sm:text-xs text-slate-400">Response Latency</p></div>
                <div><p className="text-xl sm:text-2xl font-bold text-[#0B1F3B]">3</p><p className="text-[10px] sm:text-xs text-slate-400">Languages</p></div>
                <div><p className="text-xl sm:text-2xl font-bold text-[#0B1F3B]">100%</p><p className="text-[10px] sm:text-xs text-slate-400">Voice-First</p></div>
              </div>
            </div>
            <div className="relative fade-up fade-up-delay-2">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-slate-200/60">
                <img
                  src="https://images.unsplash.com/photo-1758520144424-2e04f4ad7f60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxzYWxlcyUyMHRlYW0lMjBwcm9mZXNzaW9uYWwlMjBjb252ZXJzYXRpb24lMjBtZWV0aW5nfGVufDB8fHx8MTc3NTQ0MDU0MHww&ixlib=rb-4.1.0&q=85"
                  alt="Sales professionals in conversation"
                  className="w-full h-[260px] sm:h-[360px] lg:h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3B]/60 to-transparent" />
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3.5 sm:p-5 shadow-lg">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#14B8A6] rounded-full flex items-center justify-center flex-shrink-0">
                        <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-[#0B1F3B] truncate">Live Voice Practice</p>
                        <p className="text-[10px] sm:text-xs text-slate-400 truncate">AI customer responding in real-time...</p>
                      </div>
                      <div className="hidden xs:flex items-end gap-0.5 h-6 flex-shrink-0">
                        {[...Array(5)].map((_, i) => (
                          <div key={`wave-${i}`} className="w-1 bg-[#14B8A6] rounded-full waveform-bar" style={{ animationDelay: `${i * 0.1}s`, height: '8px' }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden sm:block absolute -top-3 -right-3 lg:-top-4 lg:-right-4 bg-[#F59E0B] text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg">
                AI-Powered
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
